import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import {
  persistReducer,
  persistStore,
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import accountReducer from './slices/accountSlice';
import authReducer, { logout } from './slices/authSlice';
import { apiSlice } from './api/apiSlice';

/**
 * Only the session itself is written to localStorage. `status` and `error` are
 * per-attempt state — persisting them would replay the last sign-in error as a
 * banner on the next page load.
 */
const authPersistConfig = {
  key: 'auth',
  version: 1,
  storage,
  whitelist: ['user', 'token', 'refreshToken', 'expiresAt', 'remember', 'isAuthenticated', 'isNigerian'],
};

/**
 * Signing out empties the auth slice, and redux-persist duly writes that empty
 * session over the stored one — but the `persist:auth` key itself stays behind
 * in localStorage. A sign-out, asked for or forced by a 401, should leave
 * nothing of the session in the browser, so the stored copy is purged outright.
 *
 * Middleware rather than part of `deauthenticate`: the persistor is created
 * from this store, so the API layer can't reach it without closing an import
 * cycle. It is read lazily — this only runs on a dispatch, long after the
 * module has finished evaluating.
 */
const purgePersistedSession = () => (next) => (action) => {
  const result = next(action);
  if (action.type === logout.type) persistor.purge();
  return result;
};

export const store = configureStore({
  reducer: {
    // Session-only: the picked account is not worth persisting (see the slice).
    account: accountReducer,
    auth: persistReducer(authPersistConfig, authReducer),
    // The RTK Query cache is deliberately not persisted: it would restore stale
    // server data with its subscription/polling bookkeeping already torn down.
    [apiSlice.reducerPath]: apiSlice.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // redux-persist dispatches these with non-serializable payloads by design.
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(apiSlice.middleware, purgePersistedSession),
  devTools: process.env.NODE_ENV !== 'production',
});

// Enables `refetchOnFocus` / `refetchOnReconnect` for endpoints that opt in.
setupListeners(store.dispatch);

export const persistor = persistStore(store);
